export interface EnrichedInventoryItem {
  id?: string;
  glass_type?: string;
  glass_reference?: string;
  description?: string;
  type?: string;
  count?: string;
  color?: any;
  [key: string]: any;
}

// Ordered by display priority; the first keyword found in the (lower-cased) type string
// wins. Categories are descriptive strings from the glass data (e.g. "Opal Sheet
// Double-rolled 3mm", "Opal Frit Powder"), so match the SINGULAR form word that actually
// appears — not plurals like "Sheets"/"Frits", which never matched and left every "Opal …"
// type colliding on a stray "Opal" keyword.
const CATEGORY_RANK: Array<{ keyword: string; rank: number }> = [
  { keyword: "sheet", rank: 0 },
  { keyword: "frit", rank: 1 },
  { keyword: "stringer", rank: 2 },
  { keyword: "rod", rank: 3 },
  { keyword: "confetti", rank: 4 },
  { keyword: "billet", rank: 5 },
  { keyword: "extra", rank: 7 },
];

export const getCategoryRank = (type?: string): number => {
  if (!type) return 6;
  const lower = type.toLowerCase();
  for (const { keyword, rank } of CATEGORY_RANK) {
    if (lower.includes(keyword)) {
      return rank;
    }
  }
  return 6;
};

export interface InventoryGroup {
  glassType: string;
  category: string;
  items: EnrichedInventoryItem[];
}

const categoryOf = (item: EnrichedInventoryItem): string => item.type ?? "Other";

export const groupInventoryItems = (items: EnrichedInventoryItem[]): InventoryGroup[] => {
  const sorted = [...items].sort((a, b) => {
    const typeCompare = (a.glass_type ?? "").localeCompare(b.glass_type ?? "");
    if (typeCompare !== 0) return typeCompare;
    const rankA = getCategoryRank(a.type);
    const rankB = getCategoryRank(b.type);
    if (rankA !== rankB) return rankA - rankB;
    // Tiebreak on the category name so items of the same category stay adjacent and
    // each (glassType, category) forms exactly one group. Distinct types can share a
    // rank (e.g. several "Opal …" types all rank as Opal); without this they interleave
    // by glass_reference and the same group is emitted repeatedly, producing duplicate
    // React keys that stop the table reconciling on search/filter changes.
    const catCompare = categoryOf(a).localeCompare(categoryOf(b));
    if (catCompare !== 0) return catCompare;
    return (a.glass_reference ?? "").localeCompare(b.glass_reference ?? "");
  });

  const groups: InventoryGroup[] = [];
  let current: InventoryGroup | null = null;

  for (const item of sorted) {
    const glassType = item.glass_type ?? "Unknown";
    const category = categoryOf(item);
    if (!current || current.glassType !== glassType || current.category !== category) {
      current = { glassType, category, items: [] };
      groups.push(current);
    }
    current.items.push(item);
  }

  return groups;
};
